import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { MessageSquarePlus, Instagram, ExternalLink } from 'lucide-react';
import { StarRating } from './StarRating';
import ReviewModal from './ReviewModal';
import useReveal from '../../hooks/useReveal';
import { reviews as seedReviews, reviewsAggregate, social } from '../../mock/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewsSection({ max = 6, heading = 'What people are saying', eyebrow = 'Loved in Melbourne', bg = 'bg-[#E9EBED]' }) {
  const [google, setGoogle] = useState(null);
  const [site, setSite] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/google-reviews`)
      .then((r) => setGoogle(r.data))
      .catch(() => setGoogle({ configured: false, reviews: [] }));
    axios
      .get(`${API}/reviews`)
      .then((r) => setSite(r.data))
      .catch(() => setSite([]));
  }, []);

  const cards = useMemo(() => {
    const g = (google?.reviews || []).map((r, i) => ({
      key: `g-${i}`,
      source: 'google',
      name: r.authorName || 'Google user',
      photo: r.authorPhotoUri,
      rating: r.rating || 5,
      text: r.text || '',
      role: r.relativeTime,
    }));
    const s = (site || []).map((r) => ({
      key: `s-${r.id}`,
      source: 'site',
      name: r.name,
      rating: r.rating,
      text: r.comment,
      role: r.role,
    }));
    const seed = seedReviews.map((r, i) => ({
      key: `seed-${i}`,
      source: 'google',
      name: r.author,
      rating: r.rating,
      text: r.quote,
      role: r.time,
    }));
    // Real Google (when configured) first; otherwise approved on-site reviews first,
    // then seed reviews to guarantee a full, polished set of at least 6.
    const combined = google?.configured ? [...g, ...s] : [...s, ...seed];
    return max ? combined.slice(0, max) : combined;
  }, [google, site, max]);

  const aggregate = google?.configured
    ? { rating: google.rating, count: google.reviewCount }
    : reviewsAggregate;

  useReveal([cards.map((c) => c.key).join('|')]);

  return (
    <section className={`py-24 sm:py-32 ${bg}`} data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="reveal text-center max-w-2xl mx-auto">
          <span className="text-[#2C3A28] text-sm font-semibold uppercase tracking-[0.2em]">
            {eyebrow}
          </span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold uppercase text-[#2C3A28]">
            {heading}
          </h2>
          {aggregate?.rating ? null : (
            <p className="mt-4 text-[#3B2A20]/70 text-lg">
              Real words from our regulars and the St Kilda community.
            </p>
          )}
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((c, i) => (
            <div
              key={c.key}
              className="reveal rounded-[2.5rem] bg-[#EDEAE0] p-7 flex flex-col hover:-translate-y-1 transition-transform duration-400"
              style={{ transitionDelay: `${i * 50}ms` }}
              data-testid="review-card"
            >
              <div className="flex items-center justify-between">
                <StarRating value={c.rating} size={18} />
                {c.source === 'google' && (
                  <span className="text-xs font-bold tracking-wide text-[#2C3A28] bg-white rounded-full px-2.5 py-1 shadow-sm">
                    Google
                  </span>
                )}
              </div>
              <p className="mt-4 text-[#2C3A28] leading-relaxed flex-1">"{c.text}"</p>
              <div className="mt-6 flex items-center gap-3">
                {c.photo ? (
                  <img
                    src={c.photo}
                    alt={c.name}
                    className="w-10 h-10 rounded-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#2C3A28] text-[#E9EBED] flex items-center justify-center font-semibold">
                    {c.name?.[0] || 'A'}
                  </div>
                )}
                <div>
                  <p className="font-semibold text-[#2C3A28]">{c.name}</p>
                  <p className="text-sm text-[#3B2A20]/60">{c.role || 'Verified visitor'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to action row */}
        <div className="reveal mt-14 flex flex-col items-center gap-4">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={social.googleReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="leave-review-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-[#2C3A28] text-white px-7 py-4 font-semibold hover:bg-[#1f2a1c] transition-all hover:-translate-y-0.5"
            >
              <MessageSquarePlus size={19} />
              Leave a review on Google
              <ExternalLink size={17} />
            </a>
            <a
              href={social.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-cta"
              className="group inline-flex items-center gap-2 rounded-full border border-[#2C3A28]/25 text-[#2C3A28] px-7 py-4 font-semibold hover:bg-[#2C3A28] hover:text-white transition-all hover:-translate-y-0.5"
            >
              <Instagram size={19} />
              Follow us on Instagram
            </a>
          </div>
        </div>
      </div>

      <ReviewModal open={open} onClose={() => setOpen(false)} />
    </section>
  );
}
